const translations = require('../translations');
const orderSummary = require('./orderSummary');

const PROMO_ENDPOINT_EL = document.getElementById('promo-code-endpoint');

const MIN_CODE_LENGTH = 3;
let summaryListenerAttached = false;
let requestSeq = 0;
let appliedNormalizedCode = '';

const hiddenPromoInput = () => document.getElementById('booking-form-promoCode');

const setMessage = (el, text, show) => {
    el.innerText = text || '';
    if (show) {
        el.classList.add('visible');
    } else {
        el.classList.remove('visible');
    }
};

const resetFieldValidationClasses = (input) => {
    input.classList.remove('is-valid', 'is-invalid', 'is-loading');
    input.removeAttribute('aria-busy');
};

const buildPromoCodeField = () => {
    if (!PROMO_ENDPOINT_EL || !PROMO_ENDPOINT_EL.dataset.url) {
        return null;
    }

    const maxLength = PROMO_ENDPOINT_EL.dataset.maxLength || '64';

    const root = document.createElement('div');
    root.classList.add('tc-website-promocode', 'clearfix');

    const label = document.createElement('label');
    label.classList.add('form-label');
    label.setAttribute('for', 'promo-code');
    label.innerHTML = translations.PROMO_CODE.label;

    const inputWrap = document.createElement('div');
    inputWrap.classList.add('input');

    const input = document.createElement('input');
    input.classList.add('form-control');
    input.setAttribute('id', 'promo-code');
    input.setAttribute('type', 'text');
    input.setAttribute('autocomplete', 'off');
    input.setAttribute('placeholder', translations.PROMO_CODE.placeholder);
    input.setAttribute('maxlength', maxLength);
    input.setAttribute('aria-describedby', 'promo-code-message');

    const message = document.createElement('div');
    message.setAttribute('id', 'promo-code-message');
    message.setAttribute('role', 'status');
    message.setAttribute('aria-live', 'polite');

    input.addEventListener('input', () => {
        const hid = hiddenPromoInput();
        const norm = input.value.trim().toUpperCase();
        if (appliedNormalizedCode && norm !== appliedNormalizedCode) {
            appliedNormalizedCode = '';
            orderSummary.removePromoDiscountRow();
            if (hid) {
                hid.value = '';
            }
        }
        resetFieldValidationClasses(input);
        setMessage(message, '', false);
    });

    input.addEventListener('blur', () => {
        runPromoValidation(input, message, false);
    });

    input.addEventListener('keydown', (e) => {
        if (e.key === 'Enter') {
            e.preventDefault();
            runPromoValidation(input, message, false);
        }
    });

    inputWrap.appendChild(input);
    root.appendChild(label);
    root.appendChild(inputWrap);
    root.appendChild(message);

    return root;
};

const runPromoValidation = (input, messageEl, silent) => {
    if (!PROMO_ENDPOINT_EL || !PROMO_ENDPOINT_EL.dataset.url) {
        return;
    }

    const raw = input.value.trim();
    const hid = hiddenPromoInput();

    if (raw.length < MIN_CODE_LENGTH) {
        appliedNormalizedCode = '';
        orderSummary.removePromoDiscountRow();
        if (hid) {
            hid.value = '';
        }
        resetFieldValidationClasses(input);
        if (!silent) {
            setMessage(messageEl, '', false);
        }
        return;
    }

    const totalBase = orderSummary.getAmountForPromoValidation();
    if (totalBase <= 0) {
        return;
    }

    const pax = orderSummary.getPromoPassengerPayload
        ? orderSummary.getPromoPassengerPayload()
        : { adultCount: 0, childAmounts: [] };

    const mySeq = ++requestSeq;
    input.classList.add('is-loading');
    input.setAttribute('aria-busy', 'true');
    if (!silent) {
        setMessage(messageEl, translations.PROMO_CODE.verifying, true);
    }

    fetch(PROMO_ENDPOINT_EL.dataset.url, {
        method: 'POST',
        body: JSON.stringify({
            promoCode: raw,
            totalAmount: totalBase,
            adultCount: pax.adultCount,
            childAmounts: pax.childAmounts,
        }),
        headers: {
            'Content-type': 'application/json; charset=UTF-8',
        },
    })
        .then((response) => response.json())
        .then((payload) => {
            if (mySeq !== requestSeq) {
                return;
            }
            input.classList.remove('is-loading');
            input.removeAttribute('aria-busy');

            if (payload.success === true && payload.data) {
                const discount = parseFloat(payload.data.discountAmount);
                const totalAfterPromo = parseFloat(payload.data.totalAmount);
                if (!(discount > 0)) {
                    handleFailure(input, messageEl, translations.PROMO_CODE.invalid, hid);
                    return;
                }
                // Afficher « appliqué » aussi en revalidation silencieuse après correction pax (ex. enfant coché)
                const showAppliedFeedback = !silent || input.classList.contains('is-invalid');
                appliedNormalizedCode = String(payload.data.promoCode || raw).toUpperCase();
                input.classList.add('is-valid');
                input.classList.remove('is-invalid');
                if (showAppliedFeedback) {
                    setMessage(messageEl, translations.PROMO_CODE.applied, true);
                } else {
                    setMessage(messageEl, '', false);
                }
                orderSummary.removePromoDiscountRow();
                orderSummary.addPromoDiscountRow(discount, totalAfterPromo);
                if (hid) {
                    hid.value = appliedNormalizedCode;
                }
                return;
            }

            const errCode = payload.error && payload.error.code ? payload.error.code : '';
            let errText =
                payload.error && payload.error.message
                    ? payload.error.message
                    : translations.PROMO_CODE.invalid;
            if (errCode === 'PROMO_FAPDE_NEED_CHILD' && translations.PROMO_CODE.fapdeNeedChild) {
                errText = translations.PROMO_CODE.fapdeNeedChild;
            } else if (errCode === 'PROMO_FAPDE_NEED_ADULT' && translations.PROMO_CODE.fapdeNeedAdult) {
                errText = translations.PROMO_CODE.fapdeNeedAdult;
            } else if (errCode === 'PROMO_INACTIVE' && translations.PROMO_CODE.inactive) {
                errText = translations.PROMO_CODE.inactive;
            }
            handleFailure(input, messageEl, errText, hid);
        })
        .catch(() => {
            if (mySeq !== requestSeq) {
                return;
            }
            input.classList.remove('is-loading');
            input.removeAttribute('aria-busy');
            handleFailure(input, messageEl, translations.PROMO_CODE.networkError, hid);
        });
};

const handleFailure = (input, messageEl, text, hid) => {
    // Ne pas effacer le code saisi : on garde la valeur pour revalider après changement pax (FAPDE)
    appliedNormalizedCode = '';
    input.classList.remove('is-valid', 'is-loading');
    input.classList.add('is-invalid');
    orderSummary.removePromoDiscountRow();
    if (hid) {
        hid.value = '';
    }
    setMessage(messageEl, text, true);
};

const initPromoCodeHook = () => {
    if (!PROMO_ENDPOINT_EL || !PROMO_ENDPOINT_EL.dataset.url) {
        return null;
    }
    if (!summaryListenerAttached) {
        summaryListenerAttached = true;
        // Dès que le résumé change (enfant coché, passager ajouté, etc.), revalider
        // si un code est encore saisi — y compris après un refus FAPDE (appliedNormalizedCode vide).
        document.addEventListener('tcwebsite:orderSummaryUpdated', () => {
            const input = document.getElementById('promo-code');
            const messageEl = document.getElementById('promo-code-message');
            if (!input || !messageEl) {
                return;
            }
            if (input.value.trim().length < MIN_CODE_LENGTH) {
                return;
            }
            runPromoValidation(input, messageEl, true);
        });
    }
    return {
        endpoint: PROMO_ENDPOINT_EL.dataset.url,
    };
};

module.exports = {
    buildPromoCodeField,
    initPromoCodeHook,
};
